import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CreatePaymentParameters,
  CreatePaymentParametersBase,
  PaymentPrices,
  PaymentService
} from '../services/payment.service';
import type { StripeElements } from '@stripe/stripe-js';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Apollo } from 'apollo-angular';
import { ApplicationService, ApplicationTier, Case, CrossSellLink, MeResponse } from '../gql/types';
import { MY_APPLICANT_CASES_QUERY } from '../gql/queries';
import { PortalExperience } from '../enums';
import { ToastrService } from 'ngx-toastr';
import { FormControlWithElementDirective } from '../../widgets/form-control-with-element.directive';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { CurrencyPipe, TitleCasePipe } from '@angular/common';
import { ModalComponent } from '../components/modal/modal.component';

interface BasePayForServiceParams extends CreatePaymentParametersBase {
  service: ApplicationService;
  tier?: never;
  tiers?: never;
}

interface WithCase {
  appCase: Case;
  cases?: Case[];
}

interface WithCases {
  cases: Case[];
  appCase?: Case;
}

type PayForServiceParams = (WithCase | WithCases) & BasePayForServiceParams;

interface WithTiers {
  tiers: ApplicationTier[];
  tier?: ApplicationTier;
}

interface WithTier {
  tier: ApplicationTier;
  tiers?: ApplicationTier[];
}

interface BasePayForTierParams extends CreatePaymentParametersBase {
  appCase: Case;
  cases?: never;
  service?: never;
}

type PayForTierParams = (WithTier | WithTiers) & BasePayForTierParams;

export type PaymentModalParams = (PayForServiceParams | PayForTierParams) & {
  errorMessage?: string;
  crossSellService?: ApplicationService;
};

@Component({
  selector: 'payment-modal',
  templateUrl: './payment-modal.component.html',
  styleUrls: ['./payment-modal.component.scss'],
  imports: [
    ModalComponent,
    FormsModule,
    FaIconComponent,
    ReactiveFormsModule,
    FormControlWithElementDirective,
    CurrencyPipe,
    TitleCasePipe
  ]
})
export class PaymentModalComponent implements OnInit {
  private paymentIntentId: string;
  private stripeElements: StripeElements;

  public checkPriceError: string = '';
  public promoCode: string = '';
  public promoCodeDescription: string = '';
  public promoCodeError: string = '';
  public promoCodeFetched: boolean = false;
  public promoCodeValid: boolean = false;
  public prices: PaymentPrices;
  public caseSelected: Case;
  public tierSelected: ApplicationTier;
  public terms = new UntypedFormControl();
  public paymentError: string;
  public paymentProcessing: boolean = false;
  public paymentSuccessful: boolean = false;
  public crossSellLink: CrossSellLink | null = null;
  public crossSellAdded: boolean = false;
  public showPromoInput: boolean = false;
  private lastCheckedPromoCode: string = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PaymentModalParams,
    private dialogRef: MatDialogRef<PaymentModalComponent>,
    private paymentService: PaymentService,
    private toastService: ToastrService,
    private apollo: Apollo
  ) {}

  async ngOnInit() {
    if (this.data.paymentIntentId) {
      this.paymentIntentId = this.data.paymentIntentId;
    }

    if (this.data.errorMessage) {
      this.showError(this.data.errorMessage);
    }

    if (this.data.appCase) {
      this.caseSelected = this.data.appCase;
    } else if (this.data.cases) {
      this.caseSelected = this.findActiveCase(this.data.cases) ?? this.data.cases[0];
    }
    if (this.data.service) {
      if (!this.data.appCase && !this.data.cases) {
        throw new Error('No case provided');
      }
      // Initialize cross-sell from service's configured links
      if (this.data.service.crossSellLinks?.length) {
        this.crossSellLink = this.data.service.crossSellLinks[0];
      }
      // If returning from a redirect with cross-sell already added
      if (this.data.crossSellService && this.crossSellLink) {
        this.crossSellAdded = true;
      }
    } else if ((this.data.tiers || this.data.tier) && this.data.appCase) {
      this.tierSelected = this.data.tiers ? this.data.tiers[0] : this.data.tier;
    } else {
      throw new Error('Not enough info provided');
    }

    let promoCode = this.data.promoCode;
    if (!promoCode) {
      const paymentMetadata = this.paymentService.getPaymentMetadata();
      if (paymentMetadata.referral_code) {
        promoCode = paymentMetadata.referral_code;
      }
    }
    if (promoCode) {
      this.promoCode = promoCode;
      this.showPromoInput = true;
      await this.checkPromo(true);
    } else {
      await this.checkPriceAndLoadElements(true);
    }
  }

  checkPromo(forceLoadElements = false) {
    // Skip if we already validated this exact code (prevents double-call from Enter + blur)
    if (this.promoCode && this.promoCode === this.lastCheckedPromoCode && this.promoCodeFetched) {
      return;
    }

    this.promoCodeDescription = '';
    this.promoCodeError = '';
    this.promoCodeFetched = false;

    if (!this.promoCode) {
      this.promoCodeValid = false;
      this.lastCheckedPromoCode = '';
      return this.checkPriceAndLoadElements(forceLoadElements);
    }

    // Don't reset promoCodeValid synchronously — keeps the submit button enabled
    // during revalidation so blur doesn't race with mousedown on Complete Purchase
    const codeBeingChecked = this.promoCode;
    this.lastCheckedPromoCode = codeBeingChecked;

    this.paymentService.checkPromo(this.data.tier?.id, this.promoCode, this.data.service?.id).then((promoCode) => {
      // Ignore stale response if user changed the code while we were checking
      if (this.promoCode !== codeBeingChecked) {
        return;
      }
      this.promoCodeFetched = true;
      if (promoCode.valid) {
        this.promoCodeValid = true;
        this.promoCodeDescription = promoCode.promotion;
      } else {
        this.promoCodeValid = false;
        this.promoCodeError = promoCode.message;
      }
      return this.checkPriceAndLoadElements(forceLoadElements);
    });
  }

  private getPaymentPayload() {
    let payload: CreatePaymentParameters;

    if (this.data.service) {
      payload = {
        applicationCaseId: this.caseSelected?.id,
        serviceId: this.data.service.id
      };
    } else if (this.tierSelected) {
      payload = {
        applicationCaseId: this.caseSelected?.id,
        applicationTierId: this.tierSelected.id
      };
    }

    payload.paymentIntentId = this.paymentIntentId;
    payload.metadata = this.data.metadata || {};
    payload.metadata.accepted_terms = this.terms.value;
    if (this.promoCodeValid && this.promoCode) {
      payload.promoCode = this.promoCode;
    }
    if (this.activeCrossSellServiceId) {
      payload.crossSellServiceId = this.activeCrossSellServiceId;
    }

    return payload;
  }

  async checkPriceAndLoadElements(forceLoadElements = false) {
    this.checkPriceError = '';
    const oldTotalPrice = this.prices?.total_price;

    try {
      this.prices = await this.paymentService.checkPrices(
        this.tierSelected?.id,
        this.promoCodeValid && this.promoCode,
        this.data.service?.id,
        this.caseSelected?.id,
        this.activeCrossSellServiceId
      );

      if (this.combinedTotal === '0.00') {
        // No need to set up elements — clear reference so next non-zero
        // total forces a fresh mount (the DOM node is destroyed by @if)
        this.stripeElements = null;
        return;
      }
    } catch (error) {
      console.error('Checking price error:', error);
      if (error.status) {
        this.checkPriceError = error.message || String(error);
      }
      return;
    }

    return await this.paymentService
      .createPayment(this.getPaymentPayload())
      .then((result) => {
        if (
          oldTotalPrice !== result.prices.total_price ||
          this.paymentIntentId != result.intent_id ||
          forceLoadElements
        ) {
          const hadElements = !!this.stripeElements;
          this.prices = {
            ...result.prices,
            cross_sell_prices: this.prices?.cross_sell_prices
          };
          this.paymentIntentId = result.intent_id;

          // this fixes race condition between us updating the price and stripe mounting the element
          setTimeout(() => {
            if (!this.prices) {
              console.error('Prices not available when mounting payment element');
              return;
            }
            if (hadElements) {
              // Elements already mounted — refresh with updated PaymentIntent amount
              // This preserves user-entered card details
              this.stripeElements.fetchUpdates();
            } else {
              // First mount — create elements from scratch
              this.stripeElements = this.paymentService.stripe.elements({
                clientSecret: result.client_secret
              });
              const paymentOptions = {
                defaultValues: this.paymentService.getStripePaymentElementDefaultValues()
              };
              const paymentElement = this.stripeElements.create('payment', paymentOptions);
              paymentElement.mount('#payment-element');
            }
          }, 0);
        }
      })
      .catch((error) => {
        console.error('Checking price error:', error);
        this.checkPriceError = error.message || String(error);
      });
  }

  async submitPayment(event) {
    event.preventDefault();
    this.paymentProcessing = true;

    if (this.combinedTotal === '0.00') {
      return this.paymentService
        .createPayment(this.getPaymentPayload())
        .then((result) => {
          this.paymentService.handleReceiptStatus(result.receipt_status, this);
          this.paymentProcessing = false;
        })
        .catch((error) => {
          console.error(error);
          this.paymentProcessing = false;
          this.paymentError = error.message || 'Unknown error, please contact support.';
        });
    }

    const base = { appCase: this.caseSelected, promoCode: this.promoCode };
    const crossSellService = this.crossSellAdded ? this.crossSellLink?.service : undefined;
    const return_url = this.paymentService.getReturnUrl(
      this.data.service
        ? { ...base, service: this.data.service, crossSellService }
        : { ...base, tier: this.tierSelected }
    );

    const { error, paymentIntent } = await this.paymentService.stripe.confirmPayment({
      elements: this.stripeElements,
      confirmParams: { return_url },
      redirect: 'if_required'
    });

    try {
      if (error) {
        console.error(error);
        this.paymentProcessing = false;
        if (
          error.code === 'payment_intent_unexpected_state' &&
          error.payment_intent &&
          error.payment_intent.status === 'succeeded'
        ) {
          await this.paymentService.handlePaymentIntentResult(
            error.payment_intent.id,
            error.payment_intent.status,
            this
          );
          this.paymentSuccessful = true;
          this.paymentProcessing = false;
        } else {
          this.toastService.error('Payment failed.');
          this.paymentProcessing = false;
        }
      } else if (paymentIntent) {
        await this.paymentService.handlePaymentIntentResult(paymentIntent.id, paymentIntent.status, this);
        this.paymentProcessing = false;
        this.paymentSuccessful = true;
      }
    } catch (error) {
      console.error(error);
      this.paymentError = error.message || 'Unknown error, please contact support.';
      this.paymentProcessing = false;
    }
  }

  get paymentButtonDisabled() {
    // be careful to keep this function super lightweight, it's used in a template expression
    return (
      !this.caseSelected ||
      !this.terms.value ||
      this.paymentProcessing ||
      this.paymentSuccessful ||
      this.checkPriceError ||
      (this.promoCode && !this.promoCodeValid)
    );
  }

  addCrossSell() {
    this.crossSellAdded = true;
    this.checkPriceAndLoadElements(true);
  }

  removeCrossSell() {
    this.crossSellAdded = false;
    this.checkPriceAndLoadElements(true);
  }

  get activeCrossSellServiceId(): string | undefined {
    return this.crossSellAdded ? this.crossSellLink?.service?.id : undefined;
  }

  get combinedTotal(): string {
    if (!this.prices) {
      return '';
    }
    let total = parseFloat(this.prices.total_price);
    if (this.crossSellAdded && this.prices.cross_sell_prices) {
      total += parseFloat(this.prices.cross_sell_prices.total_price);
    }
    return total.toFixed(2);
  }

  get combinedListedTotal(): string {
    if (!this.prices) {
      return '';
    }
    let total =
      parseFloat(this.prices.listed_platform_price || '0') + parseFloat(this.prices.listed_legal_services_price || '0');
    if (this.crossSellAdded && this.prices.cross_sell_prices) {
      total +=
        parseFloat(this.prices.cross_sell_prices.listed_platform_price || '0') +
        parseFloat(this.prices.cross_sell_prices.listed_legal_services_price || '0');
    }
    return total.toFixed(2);
  }

  get discountedTotal(): string | null {
    if (!this.prices || !this.promoCodeValid) {
      return null;
    }
    const listed = this.combinedListedTotal;
    const actual = this.combinedTotal;
    if (listed && actual && listed !== actual) {
      return actual;
    }
    return null;
  }

  showError(error: string) {
    this.paymentError = error;
  }

  // The Case in `cases` matching the user's current_applicationcase, looked up
  // from the cache populated by the dashboard's MY_APPLICANT_CASES_QUERY.
  private findActiveCase(cases: Case[]): Case | undefined {
    const data = this.apollo.client.readQuery<MeResponse>({
      query: MY_APPLICANT_CASES_QUERY,
      variables: { experienceType: PortalExperience.ForeignNational }
    });
    const activeId = data?.me?.currentApplicationcase?.id;
    return activeId ? cases.find((c) => c.id === activeId) : undefined;
  }

  close() {
    this.dialogRef.close(true);
  }
}
